import CreditService from './CreditService';

export enum CreditApplicationStatus {
  STARTED = 'started_by_customer',
  SUBMITTED = 'submitted_by_customer',
  ACCEPTED = 'accepted_by_processor',
  DELAYED = 'manual_review',
  APPROVED = 'approved_by_processor',
  REJECTED = 'rejected_by_processor'
}

let pollTimeout;
let shouldPoll = false;
const delay = ms => new Promise(res => setTimeout(res, ms));

export default class CreditApplicationClient {
  creditApplicationActive: boolean | undefined;

  canApply: boolean | undefined;

  id: number | undefined;

  monthlyHousingCost: number | null | undefined;

  yearlyIncome: number | null | undefined;

  employmentStatus: string | null | undefined;

  nonTaxableYearlyIncome: boolean | null | undefined;

  initialApr: number | null | undefined;

  initialCreditLimit: number | null | undefined;

  applicationStatus: CreditApplicationStatus | null | undefined;

  acceptedPreApprovalDocs = false;

  hasActiveCreditApplication() {
    return this.creditApplicationActive;
  }

  hasExpiredCreditApplication() {
    return this.creditApplicationActive && this.canApply;
  }

  hasSubmittedHousingInformation() {
    return (
      this.monthlyHousingCost !== undefined && this.monthlyHousingCost !== null
    );
  }

  hasSubmittedEmploymentInformation() {
    return (
      this.yearlyIncome !== undefined &&
      this.yearlyIncome !== null &&
      !!this.employmentStatus
    );
  }

  hasAcceptedPreApprovalDocs() {
    return this.acceptedPreApprovalDocs;
  }

  hasSubmittedCreditApplication() {
    // Should revisit this logic when we get to submitting application
    return this.applicationStatus !== CreditApplicationStatus.STARTED;
  }

  getAPR() {
    return this.initialApr;
  }

  getCreditLimit() {
    return this.initialCreditLimit;
  }

  getCreditDecision() {
    return this.applicationStatus || 'not_started';
  }

  setCreditApplicationData(creditApplication) {
    this.id = creditApplication.id;
    this.monthlyHousingCost = creditApplication.monthlyHousingCost;
    this.yearlyIncome = creditApplication.yearlyIncome;
    this.employmentStatus = creditApplication.employmentStatus;
    this.nonTaxableYearlyIncome = creditApplication.nonTaxableYearlyIncome;
    this.initialApr = creditApplication.initialApr;
    this.initialCreditLimit = creditApplication.initialCreditLimit;
    this.applicationStatus = creditApplication.latestStatus
      ? creditApplication.latestStatus.name
      : this.applicationStatus;
  }

  async getActiveCreditApplication({ authToken }: { authToken: string }) {
    try {
      const response = await CreditService.getActiveCreditApplication({
        authToken
      });
      this.creditApplicationActive = response.data.hasActiveCreditApplication;
      this.canApply = response.data.canApply;
      if (response.data.creditApplication) {
        this.setCreditApplicationData(response.data.creditApplication);
      }
    } catch (error) {
      throw error;
    }
    return this;
  }

  async pollApplicationStatus({
    pollTime,
    interval,
    authToken
  }: {
    pollTime: number;
    interval: number;
    authToken: string;
  }) {
    shouldPoll = true;
    pollTimeout = setTimeout(() => {
      shouldPoll = false;
    }, pollTime);
    return this.poll(authToken, interval);
  }

  async poll(token: string, interval: number) {
    return this.getActiveCreditApplication({ authToken: token }).then(
      async (res: CreditApplicationClient) => {
        if (
          res.applicationStatus === CreditApplicationStatus.APPROVED ||
          res.applicationStatus === CreditApplicationStatus.REJECTED
        ) {
          shouldPoll = false;
          clearTimeout(pollTimeout);
          return res.applicationStatus || null;
        }

        if (shouldPoll) {
          await delay(interval);
          return this.poll(token, interval);
        }

        return res.applicationStatus || null;
      }
    );
  }

  async startCreditApplication({ authToken }: { authToken: string }) {
    try {
      const response = await CreditService.startCreditApplication({
        authToken
      });
      this.creditApplicationActive = true;
      if (response.data.creditApplication) {
        this.setCreditApplicationData(response.data.creditApplication);
      }
    } catch (error) {
      throw error;
    }
    return this;
  }

  async submitCreditApplication({ authToken }: { authToken: string }) {
    try {
      const response = await CreditService.submitCreditApplication({
        authToken
      });
      if (response.data.creditApplication) {
        this.setCreditApplicationData(response.data.creditApplication);
      }
    } catch (error) {
      throw error;
    }
    return this;
  }

  async submitHousingInformation({
    authToken,
    monthlyHousingCost
  }: {
    authToken: string;
    monthlyHousingCost: number;
  }) {
    try {
      await CreditService.submitHousingInformation({
        authToken,
        monthlyHousingCost
      });
      this.monthlyHousingCost = monthlyHousingCost;
    } catch (error) {
      throw error;
    }
    return this;
  }

  async submitEmploymentInformation({
    authToken,
    yearlyIncome,
    employmentStatus,
    nonTaxableYearlyIncome
  }: {
    authToken: string;
    yearlyIncome: number;
    employmentStatus: string;
    nonTaxableYearlyIncome: boolean;
  }) {
    try {
      await CreditService.submitEmploymentInformation({
        authToken,
        yearlyIncome,
        employmentStatus,
        nonTaxableYearlyIncome
      });
      this.yearlyIncome = yearlyIncome;
      this.employmentStatus = employmentStatus;
      this.nonTaxableYearlyIncome = nonTaxableYearlyIncome;
    } catch (error) {
      throw error;
    }
    return this;
  }

  async getApprovalDocuments({
    authToken,
    documentsType
  }: {
    authToken: string;
    documentsType: string;
  }) {
    try {
      const response = await CreditService.getApprovalDocs({
        authToken,
        documentsType
      });
      return response?.data?.documentsList;
    } catch (error) {
      throw error;
    }
  }

  async acceptPreApprovalDocs({ authToken }) {
    try {
      await CreditService.acceptPreApprovalDocs({
        authToken
      });
      this.acceptedPreApprovalDocs = true;
    } catch (error) {
      throw error;
    }
    return this;
  }
}

let client;

export function useCreditClient(): CreditApplicationClient {
  if (!client) {
    client = new CreditApplicationClient();
  }
  return client;
}
